/** The live session over long polling, for networks that refuse WebSockets.
 *
 * The same contract as `LiveSocket` (connect, send, close; the same options and callbacks),
 * talking to the session service's `/v1/sessions/{id}/live` routes instead of `/v1/ws/{id}`
 * (services/session/src/session_service/poll.py). The room cannot tell the two apart: the
 * server identifies the caller the same way and handles every message with the socket's code.
 *
 * One request is always waiting for the next frames; when it returns, the next one starts. A
 * message is sent as its own POST and its reply arrives on that waiting request, exactly where
 * the socket would have delivered it. The token travels as the bearer, never in the URL, and a
 * staff token that has expired mid-lesson is refreshed once before the connection gives up. */
import { serviceUrl } from "../api/base";
import type { ConnState, LiveSocketOptions, ServerMessage } from "./socket";

export type LivePollOptions = LiveSocketOptions & {
  /** A fresh token when the server says the current one expired (a teacher's access token);
   * null when there is nothing to refresh. Respondent and projector tokens never expire. */
  refreshToken?: () => Promise<string | null>;
  /** How long a poll is held, in seconds; the server clamps it (25). */
  waitS?: number;
};

type Opened = { conn_id: string; wait_s: number; idle_s: number; messages: ServerMessage[] };

/** The connection is gone and a new one must be opened (idle, closed, or another process). */
class Gone extends Error {}
/** The server refused the caller: stop, as the socket does on its final close codes. */
class Refused extends Error {}

export class LivePoll {
  private connId: string | null = null;
  private token: string;
  private queue: Record<string, unknown>[] = [];
  private attempts = 0;
  private closed = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private waiting: AbortController | null = null;
  private state: ConnState | null = null;
  private readonly opts: LivePollOptions;

  // plain fields rather than parameter properties: node --test strips types, it does not compile
  constructor(opts: LivePollOptions) {
    this.opts = opts;
    this.token = opts.token;
  }

  connect(): void {
    if (this.closed) return;
    void this.run();
  }

  send(payload: Record<string, unknown>): void {
    if (this.connId && !this.closed) {
      void this.post(payload).catch(() => this.enqueue(payload));
      return;
    }
    this.enqueue(payload);
  }

  close(): void {
    this.closed = true;
    if (this.timer) clearTimeout(this.timer);
    this.waiting?.abort();
    const id = this.connId;
    this.connId = null;
    if (id) {
      // best effort, like a socket closing; the server reaps it anyway when it goes quiet
      void this.request("DELETE", `/live/${id}`).catch(() => undefined);
    }
  }

  // ---- the loop ----------------------------------------------------------------------------

  private async run(): Promise<void> {
    try {
      const opened = (await this.json("POST", "/live", { device_id: this.opts.deviceId }, 201)) as Opened;
      if (this.closed) return;
      this.connId = opened.conn_id;
      this.attempts = 0;
      this.setState("live");
      this.deliver(opened.messages);
      for (const payload of this.queue.splice(0)) this.send(payload);
      for (;;) {
        if (this.closed) return;
        this.waiting = new AbortController();
        const wait = this.opts.waitS ?? opened.wait_s;
        const res = (await this.json(
          "GET",
          `/live/${opened.conn_id}?wait=${encodeURIComponent(String(wait))}`,
          undefined,
          200,
          this.waiting.signal,
        )) as { messages: ServerMessage[] };
        this.deliver(res.messages);
      }
    } catch (err) {
      if (this.closed) return;
      this.connId = null;
      if (err instanceof Refused) {
        this.setState("offline");
        return;
      }
      // Gone opens again at once; a network error backs off like the socket does
      const delay = err instanceof Gone ? 0 : Math.min(8000, 500 * 2 ** this.attempts++);
      if (!(err instanceof Gone)) this.setState("reconnecting");
      this.timer = setTimeout(() => this.connect(), delay);
    }
  }

  private deliver(messages: ServerMessage[]): void {
    for (const m of messages) {
      try {
        this.opts.onMessage(m);
      } catch {
        /* a screen's handler must not stop the loop */
      }
    }
  }

  private setState(s: ConnState): void {
    if (s === this.state) return;
    this.state = s;
    this.opts.onState(s);
  }

  private enqueue(payload: Record<string, unknown>): void {
    this.queue.push(payload);
    const fb = this.opts.fallback;
    if (!fb) return;
    void fb(payload).then((ack) => {
      if (!ack) return;
      const i = this.queue.indexOf(payload);
      if (i >= 0) this.queue.splice(i, 1);
      this.opts.onMessage(ack);
    });
  }

  private async post(payload: Record<string, unknown>): Promise<void> {
    const id = this.connId;
    if (!id) throw new Gone();
    await this.json("POST", `/live/${id}`, payload, 202);
  }

  // ---- HTTP --------------------------------------------------------------------------------

  private request(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<Response> {
    const headers: Record<string, string> = { Authorization: `Bearer ${this.token}` };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    return fetch(serviceUrl(`/session/v1/sessions/${this.opts.sessionId}${path}`), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
      // a poll must never be answered from a cache between here and the service
      cache: "no-store",
    });
  }

  private async json(method: string, path: string, body: unknown, ok: number, signal?: AbortSignal): Promise<unknown> {
    let res = await this.request(method, path, body, signal);
    if (res.status === 401 && this.opts.refreshToken) {
      const fresh = await this.opts.refreshToken();
      if (fresh) {
        this.token = fresh;
        res = await this.request(method, path, body, signal);
      }
    }
    if (res.status === ok) return ok === 204 ? null : res.json();
    if (res.status === 404) {
      const code = await res.json().then((b: { code?: string }) => b?.code, () => undefined);
      // an unknown connection is reopened; an unknown session is final
      throw code === "poll_gone" ? new Gone() : new Refused();
    }
    if (res.status === 401 || res.status === 403) throw new Refused();
    throw new Error(`HTTP ${res.status}`);
  }
}
